import { type InputHTMLAttributes, type KeyboardEvent, type ReactNode, forwardRef } from 'react';
import { Loader2 } from 'lucide-react';
import { startCase } from 'lodash';
import { KeyboardKey } from '@/lib/keyboard';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  isLoading?: boolean;
  prependIcon?: ReactNode;
  appendIcon?: ReactNode;
  withUploader?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
  indeterminate?: boolean;
  safeInputRef?: React.MutableRefObject<HTMLInputElement | null>;
  inputFieldClassName?: string;
  /** When true, pressing Enter will blur the input instead of submitting. Defaults to true. */
  blurOnEnter?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      prependIcon,
      appendIcon,
      type,
      isLoading,
      inputRef,
      safeInputRef,
      withUploader,
      blurOnEnter = true,
      inputFieldClassName,
      indeterminate,
      'aria-invalid': ariaInvalid,
      ...props
    },
    ref,
  ) => {
    const tCommon = useTranslations('common');
    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (blurOnEnter && e.key === KeyboardKey.Enter) {
        e.preventDefault();
        e.currentTarget.blur();
      }
      props.onKeyDown?.(e);
    };

    return (
      <div
        ref={ref}
        className={cn(
          'flex h-6.5 w-full items-center rounded-lg border-0 p-1.5 shadow-none ring-1 ring-ui-bevel transition duration-150 file:border-0 file:text-sm file:font-medium focus-within:outline-hidden focus-within:ring-2 focus-within:ring-ui-separator focus-within:ring-offset-0 focus-within:duration-150 has-aria-invalid:ring-red-500! has-aria-invalid:focus-within:ring-red-500!',
          className,
          indeterminate && 'bg-status-warning/25 ring-status-warning/50 dark:ring-status-warning-dark/50',
          props.disabled && 'cursor-not-allowed opacity-50', // it is not enough to just add a class with `disabled:` prefix, because div element does not have disabled attribute and the classes would never get applied
        )}
      >
        {prependIcon}
        {withUploader ? (
          <>
            <input type="file" ref={inputRef} className="hidden" readOnly={false} onInput={props.onInput} />
            <input
              aria-invalid={ariaInvalid}
              className={cn(
                'h-10 w-full cursor-pointer bg-transparent text-sm outline-hidden placeholder:text-text-gray-alternate disabled:cursor-not-allowed',
                inputFieldClassName,
              )}
              onClick={props.onClick}
              placeholder={
                indeterminate ? tCommon('multipleValuesSelected', { field: props.name ?? 'none' }) : props.placeholder
              }
              value={props.value}
              readOnly
            />
          </>
        ) : (
          <input
            type={type}
            aria-invalid={ariaInvalid}
            className={cn(
              'h-10 w-full bg-transparent text-xs outline-hidden disabled:cursor-not-allowed',
              indeterminate
                ? 'placeholder:italic placeholder:text-status-warning'
                : 'placeholder:text-text-gray-alternate',
              inputFieldClassName,
            )}
            ref={(el) => {
              if (safeInputRef) {
                safeInputRef.current = el;
              }
              if (typeof ref === 'function') {
                ref(el);
              } else if (ref) {
                (ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
              }
            }}
            {...props}
            onKeyDown={handleKeyDown}
            placeholder={
              indeterminate
                ? tCommon('multipleValuesSelected', {
                    field: props.name ? startCase(props.name.replace(/_/g, ' ')) : 'none',
                  })
                : props.placeholder
            }
          />
        )}
        {isLoading && <Loader2 className="mr-1 ml-2 icon-4 animate-spin opacity-50" strokeWidth={2.5} />}
        {appendIcon}
      </div>
    );
  },
);

Input.displayName = 'Input';

export { Input };
